// Cute pastel cat that walks slowly across the bottom of the page. Pure SVG +
// CSS keyframes — no JS animation runtime, no extra deps. The cat translates
// left→right over ~45s, then loops with a brief pause off-screen.

export function WalkingCat() {
  return (
    <div className="pointer-events-none fixed bottom-2 left-0 right-0 z-0 select-none">
      <div className="walking-cat-track">
        <CatSvg />
      </div>
      <style>{`
        .walking-cat-track {
          width: max-content;
          animation: walkingCatX 50s linear infinite;
        }
        @keyframes walkingCatX {
          0%   { transform: translateX(-80px); }
          100% { transform: translateX(calc(100vw + 80px)); }
        }
        .cat-body {
          animation: catBob 0.6s ease-in-out infinite;
          transform-origin: center bottom;
        }
        @keyframes catBob {
          0%, 100% { transform: translateY(0); }
          50%      { transform: translateY(-1px); }
        }
        .cat-tail {
          animation: catTail 1.8s ease-in-out infinite;
          transform-origin: 56px 24px;
        }
        @keyframes catTail {
          0%, 100% { transform: rotate(-4deg); }
          50%      { transform: rotate(8deg); }
        }
        .cat-leg-fl, .cat-leg-bl { animation: catLegL 0.6s ease-in-out infinite; transform-origin: top; }
        .cat-leg-fr, .cat-leg-br { animation: catLegR 0.6s ease-in-out infinite; transform-origin: top; }
        @keyframes catLegL { 0%, 100% { transform: rotate(-8deg);} 50% { transform: rotate(8deg);} }
        @keyframes catLegR { 0%, 100% { transform: rotate(8deg); } 50% { transform: rotate(-8deg);} }
        @media (prefers-reduced-motion: reduce) {
          .walking-cat-track, .cat-body, .cat-tail,
          .cat-leg-fl, .cat-leg-bl, .cat-leg-fr, .cat-leg-br {
            animation: none !important;
          }
        }
      `}</style>
    </div>
  );
}

function CatSvg() {
  return (
    <svg
      width="64"
      height="42"
      viewBox="0 0 64 42"
      xmlns="http://www.w3.org/2000/svg"
      className="cat-body"
      aria-hidden="true"
    >
      <g>
        {/* tail */}
        <path
          className="cat-tail"
          d="M 56 24 Q 62 18 60 10"
          stroke="#D4C2EC"
          strokeWidth="3.2"
          strokeLinecap="round"
          fill="none"
        />
        {/* back legs */}
        <rect className="cat-leg-bl" x="40" y="28" width="3.4" height="9" rx="1.6" fill="#C7B3E5" />
        <rect className="cat-leg-br" x="46" y="28" width="3.4" height="9" rx="1.6" fill="#C7B3E5" />
        {/* front legs */}
        <rect className="cat-leg-fl" x="14" y="28" width="3.4" height="9" rx="1.6" fill="#C7B3E5" />
        <rect className="cat-leg-fr" x="20" y="28" width="3.4" height="9" rx="1.6" fill="#C7B3E5" />
        {/* body */}
        <ellipse cx="32" cy="24" rx="22" ry="9" fill="#E8DDF5" />
        {/* head */}
        <circle cx="14" cy="18" r="9" fill="#E8DDF5" />
        {/* ears */}
        <polygon points="7,12 9,4 13,11" fill="#E8DDF5" />
        <polygon points="14,11 17,3 21,12" fill="#E8DDF5" />
        <polygon points="8.5,11 10,7 12,10.5" fill="#F5C2C9" />
        <polygon points="15.5,11 17,7 19,10.5" fill="#F5C2C9" />
        {/* eyes */}
        <circle cx="11" cy="18" r="1.1" fill="#2C2A26" />
        <circle cx="17" cy="18" r="1.1" fill="#2C2A26" />
        {/* nose + mouth */}
        <path d="M 13.5 20.5 L 14 21.2 L 14.5 20.5 Z" fill="#CC7383" />
        <path d="M 13 21.6 Q 14 22.4 15 21.6" stroke="#9B6470" strokeWidth="0.6" fill="none" strokeLinecap="round" />
        {/* whiskers */}
        <line x1="6"  y1="20" x2="2"  y2="19" stroke="#A09B94" strokeWidth="0.5" />
        <line x1="6"  y1="21" x2="2"  y2="22" stroke="#A09B94" strokeWidth="0.5" />
        <line x1="22" y1="20" x2="26" y2="19" stroke="#A09B94" strokeWidth="0.5" />
        <line x1="22" y1="21" x2="26" y2="22" stroke="#A09B94" strokeWidth="0.5" />
      </g>
    </svg>
  );
}

// Smaller "sitting + waving" cat for the login screen. The right paw lifts and
// waves in a gentle loop. Calm pace — about 2 seconds per cycle.
export function WavingCat() {
  return (
    <div className="relative inline-block select-none" aria-hidden="true">
      <svg width="120" height="120" viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg">
        {/* tail curl behind */}
        <path
          d="M 92 78 Q 108 70 100 52"
          stroke="#D4C2EC"
          strokeWidth="6"
          strokeLinecap="round"
          fill="none"
        />
        {/* body */}
        <ellipse cx="62" cy="78" rx="32" ry="26" fill="#E8DDF5" />
        {/* left front paw resting */}
        <ellipse cx="44" cy="100" rx="8" ry="6" fill="#D4C2EC" />
        {/* feet */}
        <ellipse cx="76" cy="102" rx="9" ry="6" fill="#D4C2EC" />
        {/* head */}
        <circle cx="58" cy="46" r="26" fill="#E8DDF5" />
        {/* ears outer */}
        <polygon points="38,32 42,12 52,30" fill="#E8DDF5" />
        <polygon points="64,30 74,11 78,32" fill="#E8DDF5" />
        {/* ears inner */}
        <polygon points="42,29 44,20 49,28" fill="#F5C2C9" />
        <polygon points="68,28 72,20 75,29" fill="#F5C2C9" />
        {/* eyes (closed/happy arcs) */}
        <path d="M 48 46 Q 52 50 56 46" stroke="#2C2A26" strokeWidth="2" fill="none" strokeLinecap="round" />
        <path d="M 62 46 Q 66 50 70 46" stroke="#2C2A26" strokeWidth="2" fill="none" strokeLinecap="round" />
        {/* cheeks */}
        <circle cx="44" cy="54" r="3" fill="#F5C2C9" opacity="0.7" />
        <circle cx="74" cy="54" r="3" fill="#F5C2C9" opacity="0.7" />
        {/* nose + mouth */}
        <path d="M 56 54 L 59 58 L 62 54 Z" fill="#CC7383" />
        <path d="M 54 60 Q 59 65 64 60" stroke="#9B6470" strokeWidth="1.2" fill="none" strokeLinecap="round" />
        {/* whiskers */}
        <line x1="36" y1="55" x2="26" y2="52" stroke="#A09B94" strokeWidth="0.8" />
        <line x1="36" y1="58" x2="26" y2="60" stroke="#A09B94" strokeWidth="0.8" />
        <line x1="82" y1="55" x2="92" y2="52" stroke="#A09B94" strokeWidth="0.8" />
        <line x1="82" y1="58" x2="92" y2="60" stroke="#A09B94" strokeWidth="0.8" />
        {/* waving paw */}
        <g className="waving-paw">
          <line x1="88" y1="74" x2="100" y2="40" stroke="#D4C2EC" strokeWidth="9" strokeLinecap="round" />
          <circle cx="100" cy="38" r="7" fill="#D4C2EC" />
          <circle cx="97" cy="34" r="1.5" fill="#F5C2C9" />
          <circle cx="103" cy="34" r="1.5" fill="#F5C2C9" />
          <circle cx="100" cy="42" r="1.5" fill="#F5C2C9" />
        </g>
      </svg>
      <style>{`
        .waving-paw {
          transform-origin: 88px 74px;
          animation: paw-wave 2s ease-in-out infinite;
        }
        @keyframes paw-wave {
          0%, 100% { transform: rotate(-8deg); }
          25%      { transform: rotate(14deg); }
          50%      { transform: rotate(-4deg); }
          75%      { transform: rotate(18deg); }
        }
        @media (prefers-reduced-motion: reduce) {
          .waving-paw { animation: none !important; }
        }
      `}</style>
    </div>
  );
}
